import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Button, Container, Fade } from "@mui/material";
import {
  Download,
  Email,
  Terminal,
  Bolt,
} from "@mui/icons-material";
import officePhoto from "../../resources/cory-office.jpg";
import "../../styles/components/HeroSection.css";

const sourceCode = `const portfolio = createExperience({
  stack: ["React", ".NET", "C#"],
  focus: "reliable software",
  approach: "thoughtful design",
});

portfolio.ship();`;

const sourceLines = sourceCode.split("\n");

function HeroSection() {
  const navigate = useNavigate();
  const [typedCode, setTypedCode] = useState("");

  useEffect(() => {
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (reduceMotion) {
      setTypedCode(sourceCode);
      return undefined;
    }

    let characterIndex = 0;
    let timer;

    const typeNextCharacter = () => {
      characterIndex += 1;
      setTypedCode(sourceCode.slice(0, characterIndex));

      if (characterIndex < sourceCode.length) {
        const currentCharacter = sourceCode[characterIndex - 1];
        timer = window.setTimeout(
          typeNextCharacter,
          currentCharacter === "\n" ? 180 : 28
        );
        return;
      }

      timer = window.setTimeout(() => {
        characterIndex = 0;
        setTypedCode("");
        timer = window.setTimeout(typeNextCharacter, 500);
      }, 3200);
    };

    timer = window.setTimeout(typeNextCharacter, 900);
    return () => window.clearTimeout(timer);
  }, []);

  const visibleLines = typedCode.split("\n");

  const handleResumeClick = () => {
    navigate("/resume");
  };

  const handleContactClick = () => {
    navigate("/contact");
  };

  return (
    <section className="home-hero-section-wrapper">
      <div className="hero-grid-overlay" aria-hidden="true" />
      <div className="hero-scanline" aria-hidden="true" />

      <Container>
        <div className="hero-content">
          <div className="text-content">
            <Fade in timeout={700}>
              <div className="hero-kicker">
                <Terminal fontSize="small" />
                <span>available for full-stack builds</span>
              </div>
            </Fade>

            <Fade in timeout={1000}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "3rem", md: "5.2rem" },
                  fontWeight: 800,
                  mb: 2,
                  textAlign: { xs: "center", md: "left" },
                }}
              >
                <span className="hero-title-desktop">
                  Building software with a production mindset.
                </span>
                <span className="hero-title-mobile">
                  Production-grade software.
                </span>
              </Typography>
            </Fade>

            <Fade in timeout={1300}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.15rem", md: "1.45rem" },
                  fontWeight: 400,
                  mb: 3,
                  opacity: 0.9,
                  lineHeight: 1.7,
                }}
              >
                Cory Smith is a full stack developer focused on React,
                .NET, integrations, and interfaces that make business workflows
                easier to run.
              </Typography>
            </Fade>

            <Fade in timeout={1600}>
              <div className="hero-signal-row">
                <span>React</span>
                <span>.NET</span>
                <span>APIs</span>
                <span>Automation</span>
              </div>
            </Fade>

            <Fade in timeout={2000}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: { xs: "center", md: "flex-start" },
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Button
                  className="cta-button"
                  variant="contained"
                  size="large"
                  startIcon={<Download />}
                  onClick={handleResumeClick}
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    color: "white",
                    border: "2px solid rgba(255,255,255,0.3)",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.3)" },
                  }}
                >
                  View Resume
                </Button>
                <Button
                  className="cta-button"
                  variant="outlined"
                  size="large"
                  startIcon={<Email />}
                  onClick={handleContactClick}
                  sx={{
                    color: "white",
                    borderColor: "rgba(255,255,255,0.5)",
                    "&:hover": {
                      borderColor: "white",
                      backgroundColor: "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  Start a Project
                </Button>
              </Box>
            </Fade>
          </div>

          <Fade in timeout={1300}>
            <div className="portrait-showcase">
              <img
                className="portrait-primary"
                src={officePhoto}
                alt="Cory Smith in a modern office"
              />
              <div className="portrait-hud hud-top">
                <Bolt fontSize="small" />
                <span>live build mode</span>
              </div>
              <div className="portrait-hud hud-bottom">
                <span>React</span>
                <span>.NET</span>
                <span>APIs</span>
              </div>
              <div className="circuit-line circuit-line-one" />
              <div className="circuit-line circuit-line-two" />
            </div>
          </Fade>

          <Fade in timeout={1600}>
            <div className="development-window">
              <div className="development-header">
                <div className="window-controls" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </div>
                <div className="editor-file">
                  <Terminal fontSize="small" />
                  <span>portfolio.js</span>
                </div>
                <span className="editor-status">BUILD READY</span>
              </div>
              <pre aria-label={sourceCode}>
                <code>
                  {sourceLines.map((_, lineIndex) => {
                    const line = visibleLines[lineIndex] || "";
                    const isActiveLine = lineIndex === visibleLines.length - 1;

                    return (
                      <span className="code-line" key={lineIndex}>
                        <span className="line-number" aria-hidden="true">
                          {String(lineIndex + 1).padStart(2, "0")}
                        </span>
                        <span className="line-content">
                          {line}
                          {isActiveLine && (
                            <span className="typing-cursor" aria-hidden="true" />
                          )}
                        </span>
                      </span>
                    );
                  })}
                </code>
              </pre>
              <div className="development-footer">
                <span>main*</span>
                <span>UTF-8</span>
                <span>JavaScript React</span>
              </div>
            </div>
          </Fade>
        </div>
      </Container>
    </section>
  );
}

export default HeroSection;
